import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { BarChart3, Music, Users, Search, Loader2, ChevronLeft, ChevronRight, History } from "lucide-react";
import { toast } from "sonner";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function AdminAnalytics() {
  const { token } = useAdminAuth();

  // ---- Song play counts ----
  const [songWindow, setSongWindow] = useState("0"); // 0 = all time, 7 / 30 / 90
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [songData, setSongData] = useState(null);
  const [songLoading, setSongLoading] = useState(false);

  // ---- Login log ----
  const [logSearch, setLogSearch] = useState("");
  const [logData, setLogData] = useState(null);
  const [logPage, setLogPage] = useState(1);
  const [logLoading, setLogLoading] = useState(false);

  // ---- Recent plays (who played what) ----
  const [playSearch, setPlaySearch] = useState("");
  const [playData, setPlayData] = useState(null);
  const [playPage, setPlayPage] = useState(1);
  const [playLoading, setPlayLoading] = useState(false);
  const [playIncludeAdmins, setPlayIncludeAdmins] = useState(false);

  // ---- Per-user play history dialog ----
  const [historyUser, setHistoryUser] = useState(null); // { email, name }
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchSongs = async () => {
    setSongLoading(true);
    try {
      const res = await axios.get(`${API}/admin/song-play-counts`, {
        params: { limit: 50, days: Number(songWindow) || 0 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSongData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load song plays");
    } finally {
      setSongLoading(false);
    }
  };

  const fetchLog = async (page = 1) => {
    setLogLoading(true);
    setLogPage(page);
    try {
      const res = await axios.get(`${API}/admin/login-log`, {
        params: { page, limit: 50, search: logSearch.trim() },
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load login log");
    } finally {
      setLogLoading(false);
    }
  };

  const fetchPlays = async (page = 1) => {
    setPlayLoading(true);
    setPlayPage(page);
    try {
      const res = await axios.get(`${API}/admin/play-activity`, {
        params: {
          page,
          limit: 50,
          search: playSearch.trim(),
          include_admins: playIncludeAdmins,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlayData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load play activity");
    } finally {
      setPlayLoading(false);
    }
  };

  const openUserHistory = async (user) => {
    setHistoryUser(user);
    setHistoryData(null);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/play-activity`, {
        params: {
          page: 1,
          limit: 200,
          search: user.email,
          include_admins: true, // a specific user's own history should always show
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      // Filter strictly to plays by this user_id (search by email may match
      // a user whose email is a substring of another).
      const rows = (res.data?.rows || []).filter(
        (r) => r.email?.toLowerCase() === user.email.toLowerCase()
      );
      setHistoryData({ ...res.data, rows, total: rows.length });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load user history");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchSongs();
      fetchLog(1);
      fetchPlays(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Re-fetch songs when window or include_admins changes
  useEffect(() => {
    if (token) fetchSongs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songWindow, includeAdmins]);

  // Re-fetch plays when admin-toggle flips
  useEffect(() => {
    if (token) fetchPlays(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playIncludeAdmins]);

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div className="flex items-center gap-2">
        <BarChart3 className="h-6 w-6 text-purple-600" />
        <h1 className="text-2xl font-bold">Analytics</h1>
      </div>

      {/* ===== Top played songs ===== */}
      <Card data-testid="song-plays-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Music className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-lg">Top-played songs</CardTitle>
            </div>
            <Select value={songWindow} onValueChange={setSongWindow}>
              <SelectTrigger className="w-[180px]" data-testid="song-window-select">
                <SelectValue placeholder="Time window" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">All time</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            Plays = every time a song was streamed. Unique listeners = distinct accounts who hit play.{" "}
            {!includeAdmins && (
              <span className="text-purple-700">
                Excluding plays from admin/founder/team accounts.
              </span>
            )}
            <button
              type="button"
              onClick={() => setIncludeAdmins(!includeAdmins)}
              className="ml-1 underline text-purple-700 hover:text-purple-900"
              data-testid="toggle-include-admins"
            >
              {includeAdmins ? "(Exclude my plays)" : "(Include my plays)"}
            </button>
          </p>
        </CardHeader>
        <CardContent>
          {songLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : songData?.rows?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-3">#</th>
                    <th className="py-2 pr-3">Song</th>
                    <th className="py-2 pr-3">Course / Topic</th>
                    <th className="py-2 pr-3 text-right">Plays</th>
                    <th className="py-2 pr-3 text-right">Listeners</th>
                    <th className="py-2 pr-3">Last played</th>
                  </tr>
                </thead>
                <tbody>
                  {songData.rows.map((r, i) => (
                    <tr key={r.content_id} className="border-b last:border-0 hover:bg-purple-50/40" data-testid="song-play-row">
                      <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-3 font-medium">
                        {r.title}
                        {r.is_orphaned && (
                          <Badge variant="outline" className="ml-2 text-[10px] bg-red-50 text-red-700 border-red-200">
                            deleted
                          </Badge>
                        )}
                        {r.vibe && (
                          <span className="ml-2 text-xs text-muted-foreground">· {r.vibe}</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        {r.course_name || "—"}
                        {r.topic && <span className="text-muted-foreground"> · {r.topic}</span>}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono font-semibold">{r.play_count}</td>
                      <td className="py-2 pr-3 text-right font-mono">{r.unique_listener_count}</td>
                      <td className="py-2 pr-3 text-xs text-muted-foreground">{formatDate(r.last_played_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4">No plays recorded yet for this window.</p>
          )}
        </CardContent>
      </Card>

      {/* ===== Recent plays — who played what ===== */}
      <Card data-testid="recent-plays-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-lg">Recent plays — who played what</CardTitle>
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); fetchPlays(1); }}
              className="flex items-center gap-2"
            >
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search email, name, song, course"
                  value={playSearch}
                  onChange={(e) => setPlaySearch(e.target.value)}
                  className="pl-9 w-[260px]"
                  data-testid="play-search-input"
                />
              </div>
              <Button type="submit" variant="outline" data-testid="play-search-btn">
                Filter
              </Button>
            </form>
          </div>
          <p className="text-xs text-muted-foreground">
            Each row is one stream. Click an email to see that user&apos;s full play history.{" "}
            {!playIncludeAdmins && (
              <span className="text-purple-700">
                Excluding plays from admin/founder/team accounts.
              </span>
            )}
            <button
              type="button"
              onClick={() => setPlayIncludeAdmins(!playIncludeAdmins)}
              className="ml-1 underline text-purple-700 hover:text-purple-900"
              data-testid="toggle-play-include-admins"
            >
              {playIncludeAdmins ? "(Exclude my plays)" : "(Include my plays)"}
            </button>
          </p>
        </CardHeader>
        <CardContent>
          {playLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : playData?.rows?.length ? (
            <>
              <p className="text-xs text-muted-foreground mb-2">
                {playData.total} total · showing page {playData.page} of {playData.pages}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="py-2 pr-3">When</th>
                      <th className="py-2 pr-3">User</th>
                      <th className="py-2 pr-3">Song</th>
                      <th className="py-2 pr-3">Course / Topic</th>
                    </tr>
                  </thead>
                  <tbody>
                    {playData.rows.map((r, i) => (
                      <tr
                        key={`${r.user_id}-${r.accessed_at}-${i}`}
                        className="border-b last:border-0 hover:bg-purple-50/40"
                        data-testid="recent-play-row"
                      >
                        <td className="py-2 pr-3 text-xs whitespace-nowrap">{formatDate(r.accessed_at)}</td>
                        <td className="py-2 pr-3">
                          <button
                            type="button"
                            onClick={() => openUserHistory({ email: r.email, name: r.name })}
                            className="font-mono text-xs text-purple-700 hover:text-purple-900 underline underline-offset-2 text-left"
                            data-testid="open-user-history-btn"
                            disabled={r.email === "(unknown user)"}
                          >
                            {r.email}
                          </button>
                          {r.name && <div className="text-[11px] text-muted-foreground">{r.name}</div>}
                        </td>
                        <td className="py-2 pr-3">
                          <span className="font-medium">{r.title}</span>
                          {r.is_orphaned && (
                            <Badge variant="outline" className="ml-2 text-[10px] bg-red-50 text-red-700 border-red-200">
                              deleted
                            </Badge>
                          )}
                          {r.vibe && (
                            <span className="ml-2 text-xs text-muted-foreground">· {r.vibe}</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs">
                          {r.course_name || "—"}
                          {r.topic && <span className="text-muted-foreground"> · {r.topic}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {playData.pages > 1 && (
                <div className="flex items-center justify-between mt-3">
                  <Button
                    variant="outline" size="sm"
                    disabled={playPage <= 1}
                    onClick={() => fetchPlays(playPage - 1)}
                    data-testid="play-prev-btn"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Page {playData.page} of {playData.pages}
                  </span>
                  <Button
                    variant="outline" size="sm"
                    disabled={playPage >= playData.pages}
                    onClick={() => fetchPlays(playPage + 1)}
                    data-testid="play-next-btn"
                  >
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground py-4">
              No plays recorded yet. Streams will appear here as users listen.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ===== Login log ===== */}
      <Card data-testid="login-log-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-lg">Login log</CardTitle>
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); fetchLog(1); }}
              className="flex items-center gap-2"
            >
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter by email or name"
                  value={logSearch}
                  onChange={(e) => setLogSearch(e.target.value)}
                  className="pl-9 w-[240px]"
                  data-testid="login-search-input"
                />
              </div>
              <Button type="submit" variant="outline" data-testid="login-search-btn">
                Filter
              </Button>
            </form>
          </div>
          <p className="text-xs text-muted-foreground">
            Successful logins, newest first. Failed login attempts are not recorded.
          </p>
        </CardHeader>
        <CardContent>
          {logLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : logData?.rows?.length ? (
            <>
              <p className="text-xs text-muted-foreground mb-2">
                {logData.total} total · showing page {logData.page} of {logData.pages}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="py-2 pr-3">When</th>
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Name</th>
                      <th className="py-2 pr-3">Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logData.rows.map((r, i) => (
                      <tr key={`${r.user_id}-${i}`} className="border-b last:border-0 hover:bg-purple-50/40" data-testid="login-log-row">
                        <td className="py-2 pr-3 text-xs whitespace-nowrap">{formatDate(r.logged_in_at)}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{r.email}</td>
                        <td className="py-2 pr-3 text-xs">{r.name || "—"}</td>
                        <td className="py-2 pr-3">
                          <Badge
                            variant="outline"
                            className={r.method === "google"
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-purple-50 text-purple-700 border-purple-200"}
                          >
                            {r.method || "—"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {logData.pages > 1 && (
                <div className="flex items-center justify-between mt-3">
                  <Button
                    variant="outline" size="sm"
                    disabled={logPage <= 1}
                    onClick={() => fetchLog(logPage - 1)}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Page {logData.page} of {logData.pages}
                  </span>
                  <Button
                    variant="outline" size="sm"
                    disabled={logPage >= logData.pages}
                    onClick={() => fetchLog(logPage + 1)}
                  >
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground py-4">
              No logins recorded yet. Logins from this point forward will appear here.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ===== Per-user play history dialog ===== */}
      <Dialog
        open={!!historyUser}
        onOpenChange={(open) => { if (!open) { setHistoryUser(null); setHistoryData(null); } }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="user-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-purple-600" />
              Play history
            </DialogTitle>
            <DialogDescription>
              {historyUser && (
                <span>
                  <span className="font-mono text-purple-700">{historyUser.email}</span>
                  {historyUser.name && <span className="text-muted-foreground"> · {historyUser.name}</span>}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto -mx-6 px-6">
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              </div>
            ) : historyData?.rows?.length ? (
              <>
                <p className="text-xs text-muted-foreground mb-2">
                  {historyData.total} {historyData.total === 1 ? "play" : "plays"}
                  {historyData.total >= 200 && " (showing most recent 200)"}
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="py-2 pr-3">When</th>
                      <th className="py-2 pr-3">Song</th>
                      <th className="py-2 pr-3">Course / Topic</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.rows.map((r, i) => (
                      <tr key={`hist-${r.accessed_at}-${i}`} className="border-b last:border-0" data-testid="user-history-row">
                        <td className="py-2 pr-3 text-xs whitespace-nowrap">{formatDate(r.accessed_at)}</td>
                        <td className="py-2 pr-3">
                          <span className="font-medium">{r.title}</span>
                          {r.is_orphaned && (
                            <Badge variant="outline" className="ml-2 text-[10px] bg-red-50 text-red-700 border-red-200">
                              deleted
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs">
                          {r.course_name || "—"}
                          {r.topic && <span className="text-muted-foreground"> · {r.topic}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-4">No plays found for this user.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
